import {SocketClient} from "cbcore-ts"
import {NO, UIColor, UIRoute, UITextView, UIView, UIViewController, YES} from "uicore-ts"
import {RHCore} from "../Core/RHCore"
import {CBDialogViewShower} from "../Custom components/CB/CBDialogViewShower"
import {RHFocusManager} from "../Custom components/RH/Keyboard navigation/RHFocusManager"
import {RHPageFocusManager} from "../Custom components/RH/Keyboard navigation/RHPageFocusManager"
import {RHButton} from "../Custom components/RH/RHButton"
import {RHAlertBannerView} from "../Custom components/RH/RHAlertBannerView"
import {RHColor} from "../Custom components/RH/RHColor"
import {RHButtons, RHColors, RHFont, RHSpacing} from "../Custom components/RH/Utils_slop/RHDesignTokens"
import {RHNewProcurementPreviewViewTokens} from "../Custom components/RH/Utils_slop/RHViewTokens"
import {ProcurementDetailsViewController} from "../ProcurementDetails/ProcurementDetailsViewController"
import {RHProcurementDetailsView} from "../ProcurementDetails/RHProcurementDetailsView"
import {InvitedCompany, RHCPVSummationReport, RHProcurementPublicationVisibility, RHProcurementTemplateLockCategory} from "../SocketClientFunctions"
import type { RHProcurement } from "../../../models/prodModels"
import {UserMainViewController} from "../UserMain/UserMainViewController"
import {CPVSummationApprovalRequiredView} from "./CPVSummation/CPVSummationApprovalRequiredView"
import {NewProcurementViewController} from "./NewProcurementViewController"
import { messageOfError } from "../Core/RHErrorMessage"
import { referenceId } from "../Core/RHReference"


export class NewProcurementPreviewViewController extends UIViewController {

    static readonly _cpvSummationPublishApprovalGateEnabled = NO

    // ─── Header ───────────────────────────────────────────────────────────────

    readonly titleLabel = new UITextView(
        this.view.elementID + "TitleLabel",
        UITextView.type.header4
    ).configuredWithObject({
        text: "Hanke eelvaade",
        hoverText: "",
        backgroundColor: UIColor.transparentColor,
        textColor: RHColors.textPrimary
    }).addedAsSubviewToView(this.view)

    // ─── Procurement details ──────────────────────────────────────────────────

    readonly detailsView = new RHProcurementDetailsView()
        .addedAsSubviewToView(this.view)

    readonly publicationWarningsView = new RHAlertBannerView({
        alertType: RHAlertBannerView.type.Error
    })

    // ─── Action buttons ───────────────────────────────────────────────────────

    readonly editProcurementButton = new RHButton().addedAsSubviewToView(this.view).configuredWithObject({
        titleLabel: {
            text: "Tagasi muutmisele",
            style: { fontSize: RHFont.sizeMedium }
        },
        variant: RHButtons.exitUtilityVariant,
        contentPadding: RHButtons.compactPrimaryContentPadding,
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: async () => {
                this._rememberPreviewDraftState()
                const editorParameters: Record<string, string> = {
                    id: UIRoute.currentRoute.componentWithViewController(
                        NewProcurementPreviewViewController)!.parameters.id
                }
                if (this._backToRouteComponentName) {
                    editorParameters[NewProcurementViewController.ParameterIdentifierName.backTo] =
                        this._backToRouteComponentName
                }
                UIRoute.currentRoute
                    .routeByRemovingComponentNamed(NewProcurementPreviewViewController.routeComponentName)
                    .routeWithViewControllerComponent(NewProcurementViewController, editorParameters)
                    .apply()
            }
        }
    })

    readonly publishProcurementButton = new RHButton().addedAsSubviewToView(this.view).configuredWithObject({
        titleLabel: {
            text: "Avalda hange",
            style: { fontSize: RHFont.sizeMedium }
        },
        variant: "success",
        contentPadding: RHButtons.compactPrimaryContentPadding,
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: async () => {
                await this._confirmConflictOfInterestBeforePublishing()
            }
        }
    })

    readonly cancelDraftButton = new RHButton().addedAsSubviewToView(this.view).configuredWithObject({
        titleLabel: {
            text: "Tühista",
            style: { fontSize: RHFont.sizeMedium }
        },
        variant: "softDanger",
        contentPadding: RHButtons.compactPrimaryContentPadding,
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: () => {
                this._confirmDraftCancellation()
            }
        }
    })

    readonly footerClearanceView = new UIView().addedAsSubviewToView(this.view)

    // ─── State ────────────────────────────────────────────────────────────────

    private readonly _cpvSummationApprovalRequiredView = new CPVSummationApprovalRequiredView()
    _canModifyDraft = NO

    // ─── Focus management ─────────────────────────────────────────────────────

    override _focusManager = new RHFocusManager(() => {
        const items: (UIView | RHFocusManager)[] = []
        if (this._canModifyDraft) {
            items.push(this.editProcurementButton)
            items.push(this.publishProcurementButton)
            items.push(this.cancelDraftButton)
        }
        return items
    })

    constructor(view: UIView) {
        super(view)
        this.view.configureWithObject({ backgroundColor: RHColor.backgroundColor })
        this.detailsView.showReferenceNumber = NO
        this.detailsView.shouldShowInvitedCompanies = YES
        this.detailsView.procurementLifecycleState = "preparing"
        this._applyProtectedContentVisibility(NO)
    }

    _applyProtectedContentVisibility(visible: boolean) {
        if (!visible) {
            this.publicationWarningsView.removeFromSuperview()
        }

        const contentViews: UIView[] = [
            this.detailsView,
            this.footerClearanceView
        ]
        for (const contentView of contentViews) {
            if (visible) {
                if (contentView.superview !== this.view) {
                    contentView.addedAsSubviewToView(this.view)
                }
            }
            else {
                contentView.removeFromSuperview()
            }
        }

        const mutationButtons = [
            this.editProcurementButton,
            this.publishProcurementButton,
            this.cancelDraftButton
        ]
        for (const mutationButton of mutationButtons) {
            if (visible && this._canModifyDraft) {
                if (mutationButton.superview !== this.view) {
                    mutationButton.addedAsSubviewToView(this.view)
                }
            }
            else {
                mutationButton.removeFromSuperview()
            }
        }

    }

    _updatePublicationWarnings() {
        const procurement = this.detailsView.procurementData
        const warningMessages: string[] = []
        if (this._canModifyDraft && procurement?.status === "draft") {
            if (!procurement.files?.length) {
                warningMessages.push(
                    "Hankele ei ole lisatud ühtegi hankedokumenti. Veendu, et soovid hanke ilma lisatud dokumentideta avaldada."
                )
            }
            if (this._selectedPublicationVisibility === "public" && !this._invitedCompanies.some(company => company.isInvited)) {
                warningMessages.push(
                    "Ühtegi osalema kutsutavat ettevõtet ei ole lisatud. Kas oled kindel, et soovid nii jätkata?"
                )
            }
        }

        if (!warningMessages.length) {
            this.publicationWarningsView.removeFromSuperview()
            return
        }

        this.publicationWarningsView.text = "<strong>TÄHELEPANU:</strong><br>" +
            warningMessages.map(message => "• " + message).join("<br>")
        this.publicationWarningsView.addedAsSubviewToView(this.view)
    }

    static override readonly routeComponentName = "new_procurement_preview"
    static override readonly ParameterIdentifierName = {
        id: "id",
        backTo: "backTo"
    }
    static readonly requiredApplicationModes: ("procurer" | "offerer")[] = ["procurer"]

    override async viewWillAppear() {
        await super.viewWillAppear()
        RHPageFocusManager.shared.registerContentControllerManager(this._focusManager)
    }

    override async viewWillDisappear() {
        await super.viewWillDisappear()
        RHPageFocusManager.shared.deregisterContentControllerManager(this._focusManager)
    }

    // ─── Data loading ─────────────────────────────────────────────────────────

    async populateData() {
        const procurementID = this.procurementID
        if (!procurementID) {
            CBDialogViewShower.alert("Hange täpsustamata")
            return
        }

        this._canModifyDraft = NO
        this._applyProtectedContentVisibility(NO)

        try {
            const [procurement, capabilities] = await Promise.all([
                RHCore.sharedInstance.procurementStore.refreshProcurement(procurementID),
                RHCore.sharedInstance.procurementStore.retrieveProcurementCapabilities(procurementID)
            ])

            if (!procurement) {
                CBDialogViewShower.alert("Andmed puuduvad")
                return
            }

            if (procurement.status !== "draft") {
                if (procurement.status == "draft-cancelled") {
                    let cancelledRoute = UIRoute.currentRoute
                        .routeByRemovingComponentNamed(NewProcurementPreviewViewController.routeComponentName)
                    if (this._backToRouteComponentName) {
                        cancelledRoute = cancelledRoute.routeWithComponent(this._backToRouteComponentName, {})
                    }
                    else {
                        cancelledRoute = cancelledRoute.routeWithViewControllerComponent(UserMainViewController, {})
                    }
                    cancelledRoute.applyByReplacingCurrentRouteInHistory()
                    return
                }
                const detailsParameters: Record<string, string> = { id: procurementID }
                if (this._backToRouteComponentName) {
                    detailsParameters[ProcurementDetailsViewController.ParameterIdentifierName.backTo] =
                        this._backToRouteComponentName
                }
                UIRoute.currentRoute
                    .routeByRemovingComponentNamed(NewProcurementPreviewViewController.routeComponentName)
                    .routeWithViewControllerComponent(ProcurementDetailsViewController, detailsParameters)
                    .applyByReplacingCurrentRouteInHistory()
                return
            }

            if (!capabilities.canViewDraft) {
                CBDialogViewShower.alert("Sul puudub selle ettevalmistamisel oleva hanke vaatamise õigus.")
                return
            }

            this.detailsView.procurementData = procurement
            this._canModifyDraft = capabilities?.canModifyDraft === YES
            this._applyProtectedContentVisibility(YES)
            this._updatePublicationWarnings()

            this.view.setNeedsLayoutUpToRootView()
        }
        catch (error) {
            CBDialogViewShower.alert(
                messageOfError(error) || "Andmete laadimine ebaõnnestus"
            )
        }
    }

    get procurementID(): string | undefined {
        return UIRoute.currentRoute.componentWithViewController(NewProcurementPreviewViewController)?.parameters.id
    }


    get _selectedPublicationVisibility(): RHProcurementPublicationVisibility {
        if (this.detailsView.procurementData?.publicationVisibility === "directed") {
            return "directed"
        }
        return "public"
    }

    override async viewDidAppear() {
        await super.viewDidAppear()
        await this.populateData()
        this.view.rootView.performForcedSubtreeLayout()
    }

    get _invitedCompanies(): InvitedCompany[] {
        return this.detailsView.procurementData?.invitedCompanies ?? []
    }

    // Keep the reviewed invitation snapshot intact when saving the publication mode.
    async _savePreviewDraftState(): Promise<boolean> {
        if (!this._canModifyDraft) {
            CBDialogViewShower.alert("Sul puudub selle ettevalmistamisel oleva hanke muutmise õigus.")
            return NO
        }
        const procurementID = this.procurementID
        if (!procurementID) {
            CBDialogViewShower.alert("Hange täpsustamata")
            return NO
        }

        try {
            const currentDraft = await RHCore.sharedInstance.procurementStore.getProcurement(procurementID)
            if (!currentDraft) {
                CBDialogViewShower.alert("Hanke andmed puuduvad")
                return NO
            }

            const saveResult = await RHCore.sharedInstance.procurementStore.saveOrModifyProcurementResult(
                this._previewDraftPayloadFromProcurement(currentDraft)
            )
            if (!saveResult.accepted || !saveResult.procurement) {
                this._rememberPreviewDraftState()
                this._routeBackToEditor(
                    YES,
                    YES,
                    NewProcurementPreviewViewController._templateValidationMessage(
                        saveResult.lockCategory
                    )
                )
                return NO
            }
            return YES
        }
        catch (error) {
            console.error("Preview draft save failed:", error)
            this._rememberPreviewDraftState()
            this._routeBackToEditor(
                NO,
                YES,
                (messageOfError(error) || "Hanke salvestamine ebaõnnestus")
            )
            return NO
        }
    }

    _rememberPreviewDraftState() {
        const procurementID = this.procurementID
        if (!procurementID) {
            return
        }

        RHCore.sharedInstance.procurementStore.rememberDraftPreviewState(procurementID, {
            publicationVisibility: this._selectedPublicationVisibility,
            invitedCompanies: this._invitedCompanies
        })
    }

    _previewDraftPayloadFromProcurement(
        procurement: RHProcurement
    ): Parameters<typeof SocketClient.AddOrModifyNewProcurement>[0] {
        const responsibleProcuringUser = referenceId(
            procurement.responsibleProcuringUser ?? procurement.primaryProcuringUser
        ) ?? ""
        const authorizedProcuringUsers = (procurement.authorizedProcuringUsers ?? procurement.secondaryProcuringUsers ?? [])
            .map(user => referenceId(user))
            .filter(userId => !!userId)
        const observerProcuringUsers = (procurement.observerProcuringUsers ?? [])
            .map(user => referenceId(user))
            .filter(userId => !!userId)
        const evaluatorProcuringUsers = (procurement.evaluatorProcuringUsers ?? [])
            .map(user => referenceId(user))
            .filter(userId => !!userId)
        const fileIds = (procurement.files ?? [])
            .map(file => referenceId(file))
            .filter(fileId => !!fileId)

        return {
            _id: procurement._id,
            companyId: referenceId(procurement.procuringCompany),
            procurementType: procurement.procurementType,
            spendCategory: procurement.spendCategory,
            status: "draft",
            publicationVisibility: this._selectedPublicationVisibility,
            summationApprovalStatus: procurement.summationApprovalStatus,
            summationLastCheck: procurement.summationLastCheck,
            responsibleProcuringUser: responsibleProcuringUser,
            authorizedProcuringUsers: authorizedProcuringUsers,
            observerProcuringUsers: observerProcuringUsers,
            evaluatorProcuringUsers: evaluatorProcuringUsers,
            secondaryProcuringUsers: authorizedProcuringUsers,
            title: procurement.title,
            deadlineDate: procurement.deadlineDate,
            minimumAcceptableNumberOfOffers: procurement.minimumAcceptableNumberOfOffers,
            numberOfDaysToExtendDeadlineByIfTooFewOffers:
                procurement.numberOfDaysToExtendDeadlineByIfTooFewOffers,
            maxOffersPerPart: procurement.maxOffersPerPart,
            publishEstimatedCostOfProcurement: procurement.publishEstimatedCostOfProcurement,
            parts: procurement.parts,
            selectedLanguages: procurement.selectedLanguages ?? ["Eesti"],
            files: fileIds,
            invitedCompanies: this._invitedCompanies,
            allowUnregisteredTenderers: procurement.allowUnregisteredTenderers,
            contractDuration: procurement.contractDuration
        } as Parameters<typeof SocketClient.AddOrModifyNewProcurement>[0]
    }

    async _confirmConflictOfInterestBeforePublishing() {
        if (!this._canModifyDraft) {
            CBDialogViewShower.alert("Sul puudub selle ettevalmistamisel oleva hanke avaldamise õigus.")
            return
        }
        const procurementID = this.procurementID
        if (!procurementID) {
            CBDialogViewShower.alert("Hange täpsustamata")
            return
        }

        if (this._selectedPublicationVisibility === "directed" && !this._invitedCompanies.some(company => company.isInvited)) {
            CBDialogViewShower.alert("Mitteavaliku hanke avaldamiseks vali vähemalt üks kutsutav pakkuja.")
            return
        }

        this.publishProcurementButton.loading = YES

        try {
            if (!await this._savePreviewDraftState()) {
                return
            }

            if (NewProcurementPreviewViewController._cpvSummationPublishApprovalGateEnabled) {
                const cpvSummationReport = await this._validateCPVSummationBeforePublish(procurementID)
                if (cpvSummationReport.status === "red") {
                    this._showCPVSummationApprovalRequiredView(cpvSummationReport)
                    return
                }
            }
        }
        catch (error) {
            console.error("Preparing procurement publication failed:", error)
            this._rememberPreviewDraftState()
            this._routeBackToEditor(
                NO,
                YES,
                (messageOfError(error) || "Hange avaldamine ebaõnnestus")
            )
            return
        }
        finally {
            this.publishProcurementButton.loading = NO
        }

        const shower = CBDialogViewShower.showQuestionDialogWithSupportingText(
            "Huvide konflikti puudumise kinnitus",
            "Kinnitan, et mul puudub selle hankega seotud huvide konflikt ja avaldan hanke"
        )
        shower.dialogView.view.yesButton.titleLabel.text = "Kinnitan"
        shower.dialogView.view.noButton.titleLabel.text = "Tühista"
        shower.dialogView.view.setNeedsLayoutUpToRootView()
        shower.yesButtonWasPressed = async () => {
            shower.dialogView.dismiss()
            await this._publishProcurementAfterConflictOfInterestConfirmation(procurementID)
        }
    }

    async _publishProcurementAfterConflictOfInterestConfirmation(procurementID: string) {
        this.publishProcurementButton.loading = YES

        try {
            const publishResult = await RHCore.sharedInstance.procurementStore.publishProcurementResultForProcurementId(
                procurementID,
                this._selectedPublicationVisibility
            )
            if (!publishResult.accepted || !publishResult.procurement) {
                this._rememberPreviewDraftState()
                this._routeBackToEditor(
                    YES,
                    YES,
                    NewProcurementPreviewViewController._templateValidationMessage(
                        publishResult.lockCategory
                    )
                )
                return
            }

            CBDialogViewShower.alert("Sinu hange on avaldatud", () => {
                const detailsParameters: Record<string, string> = { id: procurementID }
                if (this._backToRouteComponentName) {
                    detailsParameters[ProcurementDetailsViewController.ParameterIdentifierName.backTo] =
                        this._backToRouteComponentName
                }
                UIRoute.currentRoute
                    .routeByRemovingComponentNamed(NewProcurementPreviewViewController.routeComponentName)
                    .routeWithViewControllerComponent(ProcurementDetailsViewController, detailsParameters)
                    .applyByReplacingCurrentRouteInHistory()
            })
        }
        catch (error) {
            console.error("Publish procurement failed:", error)
            this._rememberPreviewDraftState()
            this._routeBackToEditor(
                NO,
                YES,
                (messageOfError(error) || "Hange avaldamine ebaõnnestus")
            )
        }
        finally {
            this.publishProcurementButton.loading = NO
        }
    }

    private async _validateCPVSummationBeforePublish(
        procurementID: string
    ): Promise<RHCPVSummationReport> {
        return RHCore.sharedInstance.procurementStore.retrieveCPVSummationReportForProcurement(procurementID)
    }

    private _showCPVSummationApprovalRequiredView(report: RHCPVSummationReport) {
        this._cpvSummationApprovalRequiredView.showWithReport(report, {
            keepDraft: () => {
                CBDialogViewShower.alert("Hange jäi ettevalmistamisel olekusse. Avaldamist ei tehtud.")
            },
            editCPVCode: () => {
                this._routeBackToEditor(NO)
            },
            splitIntoParts: () => {
                this._routeBackToEditor(YES)
            },
            requestApproval: () => {
                CBDialogViewShower.alert(
                    "Kooskõlastuse taotlemise töövoog ei ole veel ühendatud. Hange jäi avaldamata."
                )
            }
        })
    }

    private _routeBackToEditor(
        focusParts: boolean,
        validateOnOpen: boolean = NO,
        validationMessage?: string
    ) {
        const procurementID = this.procurementID
        if (!procurementID) {
            CBDialogViewShower.alert("Hange täpsustamata")
            return
        }

        const editorParameters: Record<string, string> = { id: procurementID }
        if (this._backToRouteComponentName) {
            editorParameters[NewProcurementViewController.ParameterIdentifierName.backTo] =
                this._backToRouteComponentName
        }
        if (focusParts) {
            editorParameters[NewProcurementViewController.ParameterIdentifierName.focusParts] = "1"
        }
        if (validateOnOpen) {
            editorParameters[NewProcurementViewController.ParameterIdentifierName.validateOnOpen] = "1"
        }
        if (validationMessage) {
            editorParameters[NewProcurementViewController.ParameterIdentifierName.validationMessage] =
                encodeURIComponent(validationMessage)
        }

        UIRoute.currentRoute
            .routeByRemovingComponentNamed(NewProcurementPreviewViewController.routeComponentName)
            .routeWithViewControllerComponent(NewProcurementViewController, editorParameters)
            .apply()
    }


    static _templateValidationMessage(
        lockCategory?: RHProcurementTemplateLockCategory
    ): string {
        if (lockCategory === "cpv") {
            return "CPV-koodid ja hankeosade struktuur on hankemallis lukustatud."
        }
        if (lockCategory === "requiredDocuments") {
            return "Nõutud dokumendid on hankemallis lukustatud."
        }
        if (lockCategory === "invitedCompanies") {
            return "Pakkujate ring on hankemallis lukustatud."
        }
        const lockedFieldMessages: Partial<Record<RHProcurementTemplateLockCategory, string>> = {
            procurementCategory: "Hankija määratlus on hankemallis lukustatud.",
            contractingAuthorityType: "Hankija määratlus on hankemallis lukustatud.",
            publicationVisibility: "Avaldamise viis on hankemallis lukustatud.",
            deadlineDate: "Pakkumuste esitamise tähtaeg on hankemallis lukustatud.",
            deadlineExtension: "Tähtaja automaatne pikendamine on hankemallis lukustatud.",
            maxOffersPerPart: "Pakkumuste arv hankeosa kohta on hankemallis lukustatud.",
            files: "Hankedokumendid on hankemallis lukustatud.",
            parts: "Hankeosade struktuur on hankemallis lukustatud.",
            contractDuration: "Lepingu kestus on hankemallis lukustatud.",
            publishEstimatedCost: "Eeldatava maksumuse avaldamine on hankemallis lukustatud.",
            team: "Hanke meeskond on hankemallis lukustatud."
        }
        if (lockCategory && lockedFieldMessages[lockCategory]) {
            return lockedFieldMessages[lockCategory]
        }
        return "Hankemalli lukustatud välju ei õnnestunud valideerida."
    }

    // ─── Draft cancellation ───────────────────────────────────────────────────

    _confirmDraftCancellation() {
        if (!this._canModifyDraft) {
            CBDialogViewShower.alert("Sul puudub selle ettevalmistamisel oleva hanke tühistamise õigus.")
            return
        }
        const procurementID = this.procurementID
        if (!procurementID) {
            CBDialogViewShower.alert("Hange täpsustamata")
            return
        }

        const shower = CBDialogViewShower.showQuestionDialog(
            "Kas oled kindel, et soovid hanke koostamise tühistada? Hange jääb ajalukku seisundiga „Tühistatud“ " +
            "ja seda toimingut ei saa tagasi võtta."
        )
        shower.dialogView.view.yesButton.titleLabel.text = "Tühista"
        shower.yesButtonWasPressed = async () => {
            shower.dialogView.dismiss()
            await this._cancelDraftWithProcurementId(procurementID)
        }
    }

    async _cancelDraftWithProcurementId(procurementID: string) {
        try {
            const result = await RHCore.sharedInstance.procurementStore.cancelDraftProcurementWithId(procurementID)
            let route = UIRoute.currentRoute
                .routeByRemovingComponentNamed(NewProcurementPreviewViewController.routeComponentName)
            if (this._backToRouteComponentName) {
                route = route.routeWithComponent(this._backToRouteComponentName, {})
            }
            else {
                route = route.routeWithViewControllerComponent(UserMainViewController, {})
            }
            route.apply()
            if (result.isCancellationConfirmedByRefresh) {
                CBDialogViewShower.alert("Hange on juba tühistatud.")
            }
        }
        catch (error) {
            CBDialogViewShower.alert("Hanke koostamise tühistamine ebaõnnestus")
        }
    }

    // ─── Layout ───────────────────────────────────────────────────────────────

    private get _backToRouteComponentName(): string | undefined {
        return UIRoute.currentRoute
            .componentWithViewController(NewProcurementPreviewViewController)
            ?.parameters.backTo as string | undefined
    }


    override layoutViewSubviews() {
        super.layoutViewSubviews()

        const padding = this.core.paddingLength
        const bounds = this.view.contentBoundsWithInset(padding)
            .rectangleWithMaxWidth(1100, 0.5)

        const detailsFrame = bounds
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)

            .rectangleForNextRow(padding, this.detailsView)
            .assignedAsFrameOfView(this.detailsView)

        const actionButtonHeight = (RHButtons.compactHeight + 4)
        const measuredEditButtonWidth = this.editProcurementButton.titleLabel.intrinsicContentWidth(actionButtonHeight)
            + RHButtons.compactPrimaryContentPadding * 2
            + RHSpacing.large
        const measuredPublishButtonWidth = this.publishProcurementButton.titleLabel.intrinsicContentWidth(
                actionButtonHeight)
            + RHButtons.compactPrimaryContentPadding * 2
            + RHSpacing.large
        const measuredCancelButtonWidth = this.cancelDraftButton.titleLabel.intrinsicContentWidth(actionButtonHeight)
            + RHButtons.compactPrimaryContentPadding * 2
            + RHSpacing.large
        const actionButtonWidth = Math.min(
            320,
            Math.max(
                240,
                measuredEditButtonWidth,
                measuredPublishButtonWidth,
                measuredCancelButtonWidth
            )
        )

        const actionButtonsRowWidth =
            actionButtonWidth * RHNewProcurementPreviewViewTokens.actionButtonRowButtonSlots +
            RHSpacing.medium *
            (RHNewProcurementPreviewViewTokens.actionButtonRowButtonSlots - 1)

        detailsFrame
            .IF(this.publicationWarningsView.superview === this.view)
            .rectangleForNextRow(RHSpacing.large, this.publicationWarningsView)
            .assignedAsFrameOfView(this.publicationWarningsView)
            .ENDIF()
            .IF(this.editProcurementButton.superview === this.view)
            .rectangleForNextRow(RHSpacing.large, actionButtonHeight)
            .rectangleWithWidth(actionButtonsRowWidth, 0.5)
            .distributeViewsAlongWidth(
                [this.editProcurementButton, this.publishProcurementButton, this.cancelDraftButton],
                undefined,
                RHSpacing.medium,
                [actionButtonWidth, actionButtonWidth, actionButtonWidth]
            )
            .ENDIF()
            .rectangleForNextRow(
                RHNewProcurementPreviewViewTokens.footerClearanceTopGap,
                RHNewProcurementPreviewViewTokens.footerClearanceHeight
            )
            .assignedAsFrameOfView(this.footerClearanceView)
    }

}
